// screens/ConnectedAccounts.tsx — Personal › Accounts: the Google accounts a
// person has lent the cockpit (G1 of the Google connections spec, wiki
// `spec-google-connections-in-vowric-core-calendar-contacts-tasks-one-drive-folder-`).
//
// One row per account: its address, its health in words, what it is lent for,
// and the two verbs a person needs — reconnect when Google stopped honouring
// it, disconnect when they want us to stop. Connecting is a full-page trip to
// Google (the server's `/auth/google/connect`), never a fetch: Google must see
// the browser. It comes back to `#/personal/accounts/<outcome>`, and the
// outcome is said here as a receipt naming the account.
//
// ⚑ NOTHING HERE PROMISES A SYNC THE SERVER DOES NOT RUN. G1's first step
// stores the connection; the calendar/contacts jobs arrive with the live
// adapters. So a row says when it was connected, and "last synced" appears
// only once a sync run has actually read Google (`lastOkAt`, written by the
// jobs alone).
import { useCallback, useEffect, useState } from 'preact/hooks';
import {
  ApiError,
  disconnectGoogleConnection,
  getGoogleConnections,
  reconnectGoogleConnection,
  updateGoogleConnection,
  type GoogleConnectionApi,
  type GoogleConnectionsApi,
  type GoogleFeatureApi,
} from '../lib/api';
import { failureOf } from './personalShared';
import './ConnectedAccounts.css';

/** The features G1/G2 run (Drive joins with G3). Mail (G4) is offered only where the server
 * says it may be turned on (`mailAvailable`) — see featuresFor(). */
const FEATURES: Array<{ id: GoogleFeatureApi; label: string }> = [
  { id: 'calendar', label: 'Calendar' },
  { id: 'contacts', label: 'Contacts' },
  { id: 'tasks', label: 'Tasks' },
];

const MAIL: { id: GoogleFeatureApi; label: string } = { id: 'mail', label: 'Mail' };
function featuresFor(mailAvailable: boolean | undefined): Array<{ id: GoogleFeatureApi; label: string }> {
  return mailAvailable ? [...FEATURES, MAIL] : FEATURES;
}

const FEATURE_WORD: Record<GoogleFeatureApi, string> = {
  calendar: 'calendar',
  contacts: 'contacts',
  mail: 'mail',
  tasks: 'tasks',
  drive: 'Drive',
};

function dateLabel(iso: string): string {
  return new Date(iso).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
}

function healthWord(c: GoogleConnectionApi): string {
  if (c.status === 'active') return 'Connected';
  if (c.status === 'needs_reconnect') return `Google stopped accepting this connection${c.statusReason ? ` — ${c.statusReason}` : ''}`;
  return 'Disconnected';
}

/** The receipt for the trip to Google, from the route's outcome segment. */
function outcomeLine(outcome: string | undefined, newest: GoogleConnectionApi | undefined, mailAvailable?: boolean): string | null {
  if (outcome === 'declined') return 'Nothing connected — Google’s screen closed without allowing access.';
  if (!newest) return null;
  if (outcome === 'connected') {
    const lent = newest.features.map((f) => FEATURE_WORD[f]).join(' and ');
    return `${newest.email} connected${lent ? ` for ${lent}` : ''}.`;
  }
  if (outcome === 'partial') {
    const missing = featuresFor(mailAvailable).filter((f) => !newest.features.includes(f.id)).map((f) => FEATURE_WORD[f.id]);
    return `${newest.email} connected without ${missing.join(' or ')} — Google was not given permission for ${
      missing.length > 1 ? 'them' : 'it'
    }.`;
  }
  return null;
}

export function ConnectedAccounts({ tenantId, outcome }: { tenantId: string; outcome?: string }) {
  const [data, setData] = useState<GoogleConnectionsApi | null>(null);
  const [failure, setFailure] = useState<string | null>(null);
  const [receipt, setReceipt] = useState<string | null>(null);
  const [confirming, setConfirming] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);

  const load = useCallback(() => {
    getGoogleConnections(tenantId)
      .then((d) => {
        setData(d);
        setFailure(null);
      })
      .catch((err: unknown) => setFailure(failureOf(err)));
  }, [tenantId]);

  useEffect(load, [load]);

  useEffect(() => {
    if (!data) return;
    const newest = [...data.connections].sort((a, b) => b.connectedAt.localeCompare(a.connectedAt))[0];
    setReceipt(outcomeLine(outcome, newest, data.mailAvailable));
  }, [data, outcome]);

  function go(path: string) {
    window.location.assign(path);
  }

  async function reconnect(c: GoogleConnectionApi) {
    setBusy(c.id);
    try {
      go(await reconnectGoogleConnection(tenantId, c.id));
    } catch (err) {
      setFailure(failureOf(err));
      setBusy(null);
    }
  }

  async function disconnect(c: GoogleConnectionApi) {
    setBusy(c.id);
    try {
      const r = await disconnectGoogleConnection(tenantId, c.id);
      setReceipt(
        r.revokedAtGoogle
          ? `${c.email} disconnected — Google has withdrawn our access.`
          : `${c.email} disconnected here, but Google did not confirm. Remove “Vowric” at myaccount.google.com › Security to be sure.`
      );
      setConfirming(null);
      load();
    } catch (err) {
      setFailure(failureOf(err));
    } finally {
      setBusy(null);
    }
  }

  async function toggle(c: GoogleConnectionApi, feature: GoogleFeatureApi) {
    const next = c.features.includes(feature) ? c.features.filter((f) => f !== feature) : [...c.features, feature];
    setBusy(c.id);
    try {
      await updateGoogleConnection(tenantId, c.id, { features: next });
      load();
    } catch (err) {
      // A feature Google never granted: the server hands back the consent door.
      if (err instanceof ApiError && err.code === 'consent_needed') {
        const path = (err.body as { error?: { connectPath?: string } } | undefined)?.error?.connectPath;
        if (path) return go(path);
      }
      setFailure(failureOf(err));
    } finally {
      setBusy(null);
    }
  }

  async function chooseCalendars(c: GoogleConnectionApi, calendarId: string) {
    const selected = c.calendarsIn.filter((k) => k.selected).map((k) => k.id);
    const next = selected.includes(calendarId) ? selected.filter((id) => id !== calendarId) : [...selected, calendarId];
    setBusy(c.id);
    try {
      await updateGoogleConnection(tenantId, c.id, { calendarsSelected: next });
      load();
    } catch (err) {
      setFailure(failureOf(err));
    } finally {
      setBusy(null);
    }
  }

  if (failure && !data) return <div class="personal__failure">Connected accounts could not be read — {failure}</div>;
  if (!data) return <div class="gaccounts__skeleton" aria-hidden="true" />;
  if (!data.configured || !data.enabled) {
    return <div class="personal__empty">Google accounts are not turned on for this workspace.</div>;
  }

  return (
    <div class="gaccounts">
      {receipt ? (
        <div class="personal__receipt" role="status">
          {receipt}
        </div>
      ) : null}
      {failure ? <div class="personal__failure">{failure}</div> : null}
      {data.connections.length === 0 ? <div class="personal__empty">No Google accounts connected.</div> : null}
      <ul class="gaccounts__list">
        {data.connections.map((c) => {
          const synced = c.lastOkAt;
          return (
            <li class={`gaccounts__row gaccounts__row--${c.status}`} key={c.id}>
              <div class="gaccounts__head">
                <span class="gaccounts__email">{c.email}</span>
                <span class="gaccounts__health">{healthWord(c)}</span>
              </div>
              <div class="gaccounts__when">
                {c.status === 'revoked' && c.revokedAt
                  ? `Disconnected ${dateLabel(c.revokedAt)}`
                  : synced
                    ? `Last synced ${dateLabel(synced)}`
                    : `Connected ${dateLabel(c.connectedAt)}`}
              </div>
              {c.status !== 'revoked' ? (
                <div class="gaccounts__features" role="group" aria-label={`What ${c.email} is used for`}>
                  {featuresFor(data.mailAvailable).map((f) => (
                    <label class="gaccounts__feature" key={f.id}>
                      <input
                        type="checkbox"
                        checked={c.features.includes(f.id)}
                        disabled={busy === c.id || c.status !== 'active'}
                        onChange={() => void toggle(c, f.id)}
                      />
                      {f.label}
                    </label>
                  ))}
                </div>
              ) : null}
              {c.status === 'active' && c.features.includes('calendar') && c.calendarsIn.length > 0 ? (
                <div class="gaccounts__calendars" role="group" aria-label={`Calendars from ${c.email}`}>
                  {c.calendarsIn.map((k) => (
                    <label class="gaccounts__feature" key={k.id}>
                      <input
                        type="checkbox"
                        checked={k.selected}
                        disabled={busy === c.id}
                        onChange={() => void chooseCalendars(c, k.id)}
                      />
                      {k.summary}
                    </label>
                  ))}
                </div>
              ) : null}
              <div class="gaccounts__verbs">
                {c.status !== 'active' ? (
                  <button type="button" class="personal__primary" disabled={busy === c.id} onClick={() => void reconnect(c)}>
                    {c.status === 'revoked' ? 'Connect again' : `Reconnect ${c.email}`}
                  </button>
                ) : null}
                {c.status !== 'revoked' ? (
                  confirming === c.id ? (
                    <>
                      <button type="button" class="personal__secondary gaccounts__danger" disabled={busy === c.id} onClick={() => void disconnect(c)}>
                        Disconnect {c.email}
                      </button>
                      <button type="button" class="personal__secondary" onClick={() => setConfirming(null)}>
                        Keep it
                      </button>
                    </>
                  ) : (
                    <button type="button" class="personal__secondary" onClick={() => setConfirming(c.id)}>
                      Disconnect
                    </button>
                  )
                ) : null}
              </div>
            </li>
          );
        })}
      </ul>
      <div class="personal__actions">
        <button type="button" class="personal__primary" onClick={() => go(data.connectPath)}>
          Connect a Google account
        </button>
      </div>
    </div>
  );
}
